/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 10th 2022, 10:25:32 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import {
  UserLoginCredentialsValidationSchema,
  SignUpInput,
  SignUpInputValidationSchema,
  ResetPasswordInput,
  ResetPasswordInputValidationSchema,
  ResetPasswordConfirmationInput,
  ResetPasswordConfirmationInputValidationSchema,
} from "lib/schemas";
import { APISERVICEPATHS, useAPIMutation, useAPIRequest, useAPIForm } from "lib/api";
import { useNavigate } from "react-router-dom";
import { URLS } from "core/routes";
import { getAbsoluteURL, getPathWithoutTrailingParams } from "core/routes/utils";
import { useLocalSaveLoggedInUser } from "authentication/hooks/useLocalSaveLoggedInUser";

const authenticationEndPoint = APISERVICEPATHS.sessionAuth;

export const useAPITerminateUserSession = () => {
  const { deleteData: terminateUserSession, isSuccessfulAPIRequest } = useAPIMutation({
    apiServicePath: authenticationEndPoint,
  });

  // Redirect to login page using native JS location API so that app state can start afresh.
  React.useEffect(() => {
    isSuccessfulAPIRequest && window.location.assign(URLS.login);
  }, [isSuccessfulAPIRequest]);
  return { terminateUserSession };
};

interface LoginCredentials {
  username: string;
  password: string;
}

interface LoginResponse {
  session_warning_timeout: number;
  session_expiry_timeout: number;
  [key: string]: any;
}

export const useAPILoginForm = (options?: { nextURL?: string }) => {
  const { nextURL = process.env.REACT_APP_LOGIN_REDIRECT_URI } = options ?? {};

  const loginCredentials: LoginCredentials = { username: "", password: "" };

  const [loginError, setLoginError] = React.useState("");

  const handleSuccessfulLogin = (apiLoginResponse: LoginResponse) => {
    localStorage.setItem("session_warning_timeout_ms", (apiLoginResponse?.session_warning_timeout * 1000).toString());
    localStorage.setItem("session_expiry_timeout_ms", (apiLoginResponse?.session_expiry_timeout * 1000).toString());
    fetchUserDetails();
  };

  const useAPIFormReturn = useAPIForm<LoginCredentials, LoginResponse>({
    apiInitialFetchId: null,
    primaryKey: null,
    apiFormDefaultValues: loginCredentials,
    apiFormDataValidationSchema: UserLoginCredentialsValidationSchema,
    setAPIFormResponse: handleSuccessfulLogin,
    apiServicePath: authenticationEndPoint,
    errorNotificationMessage: `Login Failed: ${loginError}`,
    successNotificationMessage: "Login successful. Kindly wait as we redirect you...",
  });

  const { isSendingDataToAPI: isLoggingInUser } = useAPIFormReturn;

  const { apiDataSendErrors, isAPIDataSendError } = useAPIFormReturn;

  React.useEffect(() => {
    if (isAPIDataSendError) {
      setLoginError(apiDataSendErrors?.detail ?? "");
    }
  }, [apiDataSendErrors?.detail, isAPIDataSendError]);

  const { fetchUserDetails, isFetchingUserDetails, loggedInUser } = useLocalSaveLoggedInUser();

  return { useAPIFormReturn, isLoggingInUser, isFetchingUserDetails, loggedInUser, nextURL };
};

const signUpEndpoint = APISERVICEPATHS.adminSignup;

export const useAPISignUpForm = () => {
  const initialSignUpInput: SignUpInput = {
    email_address: "",
    confirm_email_address: "",
    organization_name: "",
    verification_base_url: `${getAbsoluteURL(getPathWithoutTrailingParams(URLS.workerAcceptInvitation))}`,
  };

  const [signUpError, setSignUpError] = React.useState("");

  const [, setSignUpAPIResponse] = React.useState<any>();

  const useAPIFormReturn = useAPIForm<SignUpInput, any>({
    apiInitialFetchId: null,
    primaryKey: null,
    apiFormDefaultValues: initialSignUpInput,
    apiFormDataValidationSchema: SignUpInputValidationSchema,
    setAPIFormResponse: setSignUpAPIResponse,
    apiServicePath: signUpEndpoint,
    errorNotificationMessage: `SignUp Failed: ${signUpError}`,
    successNotificationMessage: "Redirecting...",
  });

  const { isSuccessfulAPIDataSend, apiDataSendErrors, isAPIDataSendError } = useAPIFormReturn;

  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAPIDataSendError) {
      setSignUpError(apiDataSendErrors?.detail ?? "");
    }
  }, [apiDataSendErrors?.detail, isAPIDataSendError]);

  React.useEffect(() => {
    isSuccessfulAPIDataSend && navigate(URLS.verificationEmailSent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessfulAPIDataSend]);

  return { useAPIFormReturn };
};

const resetPasswordEndpoint = APISERVICEPATHS.resetPassword;

export const useResetPasswordAPIForm = () => {
  const useAPIFormReturn = useAPIForm<ResetPasswordInput, any>({
    apiInitialFetchId: null,
    primaryKey: null,
    apiFormDefaultValues: {
      email: null,
      password_confirmation_base_url: `${getAbsoluteURL(getPathWithoutTrailingParams(URLS.resetPasswordConfirmation))}`,
    },
    apiFormDataValidationSchema: ResetPasswordInputValidationSchema,
    setAPIFormResponse: null,
    apiServicePath: resetPasswordEndpoint,
    successNotificationMessage: "Success! Check your email for the password reset instructions",
  });

  return { useAPIFormReturn };
};

const resetPasswordConfirmationEndpoint = APISERVICEPATHS.resetPasswordConfirmation;

type UseResetPasswordCofirmationAPIFormOptions = Pick<ResetPasswordConfirmationInput, "uid" | "token">;

export const useResetPasswordCofirmationAPIForm = ({ uid, token }: UseResetPasswordCofirmationAPIFormOptions) => {
  const [errorMessage, SetErrorMessage] = React.useState<string>(null);

  const useAPIFormReturn = useAPIForm<ResetPasswordConfirmationInput, any>({
    apiInitialFetchId: null,
    primaryKey: null,
    apiFormDefaultValues: {
      uid,
      token,
      new_password1: null,
      new_password2: null,
    },
    apiFormDataValidationSchema: ResetPasswordConfirmationInputValidationSchema,
    setAPIFormResponse: null,
    apiServicePath: resetPasswordConfirmationEndpoint,
    successNotificationMessage: "You have successfully reset your password.",
    errorNotificationMessage: errorMessage,
  });

  const { apiDataSendErrors, isAPIDataSendError, isSuccessfulAPIDataSend } = useAPIFormReturn;

  React.useEffect(() => {
    if (!isAPIDataSendError) return;

    if (apiDataSendErrors?.token)
      SetErrorMessage(
        "This password confirmation link has either expired or is invalid. If you have already entered your new password, Kindly try to login. If you have still forgotten your password, kindly try to reset again for a new password confirmation link to be sent to your email."
      );
  }, [isAPIDataSendError, apiDataSendErrors?.token]);

  const navigate = useNavigate();

  React.useEffect(() => {
    if (isSuccessfulAPIDataSend) navigate(URLS.login);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessfulAPIDataSend]);

  return { useAPIFormReturn };
};

export const useSessionPingAPI = () => {
  const {
    makeRequest,
    isMakingAPIRequest: isPinging,
    isSuccessfulAPIRequest: isSuccessfulPing,
  } = useAPIRequest({
    apiServicePath: APISERVICEPATHS.sessionPing,
    makeRequestOnInitialMount: false,
  });

  const pingServer = () => makeRequest({ setValidAPIResponse: () => {} });

  return { pingServer, isPinging, isSuccessfulPing };
};
